import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { parseApiError } from 'shared/api/apiError'
import reportApiFailure from 'shared/api/reportApiFailure'
import useUserContext from 'shared/users/contexts/useUserContext'
import { createNewsPost } from '../api/createNewsPost'
import { deleteNewsPost } from '../api/deleteNewsPost'
import { useNewsPostsQuery } from '../api/fetchNewsPosts'
import { setNewsPostPinned } from '../api/setNewsPostPinned'
import { updateNewsPost } from '../api/updateNewsPost'
import { invalidateNewsCaches } from '../keys'
import { isNewsPostGone, newsErrorMessage } from '../models/newsErrorMessage'
import { NewsPost, NewsPostCreateRequest, NewsPostUpdateRequest } from '../models/types'

export interface UseNewsFeedResult {
  posts: NewsPost[]
  hasMore: boolean
  loadMore: () => void
  isLoading: boolean
  isSuccess: boolean
  isFetchingNextPage: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => void
  addPost: (draft: NewsPostCreateRequest) => Promise<NewsPost>
  updatePost: (id: number, draft: NewsPostUpdateRequest) => Promise<NewsPost | null>
  invalidateNews: () => Promise<void>
  deletePost: (id: number) => Promise<void>
  setPinned: (id: number, isPinned: boolean) => Promise<void>
}

function useNewsFeed(): UseNewsFeedResult {
  const { t } = useTranslation()
  const { user } = useUserContext()
  const queryClient = useQueryClient()
  const query = useNewsPostsQuery(!!user)

  const pages = query.data?.pages
  // De-duplicated by id: offset paging can serve the same post on two pages when someone publishes
  // while the reader is paging, and duplicate React keys would render the card twice.
  const posts = useMemo(() => {
    const seen = new Set<number>()
    return (pages ?? []).flatMap((page) => page.posts).filter((post) => {
      if (seen.has(post.id)) {
        return false
      }
      seen.add(post.id)
      return true
    })
  }, [pages])

  const invalidateNews = () => invalidateNewsCaches(queryClient)

  const run = async <T>(operation: () => Promise<T>): Promise<T> => {
    try {
      const result = await operation()
      await invalidateNews()
      return result
    } catch (error) {
      reportApiFailure(error, (apiError) => toast.error(t(newsErrorMessage(apiError))))
      if (parseApiError(error).reason === 'notFound') {
        await invalidateNews()
      }
      throw error
    }
  }

  return {
    posts,
    hasMore: !!query.hasNextPage,
    loadMore: () => { query.fetchNextPage() },
    // An anonymous mount is a redirect in progress, not an empty feed — useAuthGuard is on its way.
    isLoading: !user || query.isInitialLoading,
    isSuccess: query.isSuccess,
    isFetchingNextPage: query.isFetchingNextPage,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: () => { query.refetch() },
    // The server starts the e-mail fan-out after the post commits, so a successful create is all
    // the client can report.
    addPost: async (draft) => {
      const created = await run(() => createNewsPost(draft))
      if (draft.sendNotification) {
        toast.success(t('news.notifyQueued'))
      }
      return created
    },
    // A post deleted under the admin's feet resolves to null rather than rejecting, so the form can
    // lock itself instead of offering a save that can no longer succeed. The feed was already refreshed.
    updatePost: async (id, draft) => {
      try {
        return await run(() => updateNewsPost(id, draft))
      } catch (error) {
        if (!isNewsPostGone(error)) {
          throw error
        }
        return null
      }
    },
    invalidateNews,
    // Swallowed after the toast: there is nothing further the admin can do about a failed pin or
    // delete, and rejecting would only produce an unhandled rejection from the click handler.
    deletePost: async (id) => {
      await run(() => deleteNewsPost(id)).catch(() => undefined)
    },
    setPinned: async (id, isPinned) => {
      await run(() => setNewsPostPinned(id, isPinned)).catch(() => undefined)
    },
  }
}

export default useNewsFeed
